"use client";

import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { faChevronDown, FontAwesomeIcon } from '@/constants/icons';
import { getBaseImageUrl, getEventsHref, getMerchantHref, getPromotionHref } from '@/constants/hooks';

interface Props {
  merchantData: any[];
  categories: any[];
  mer_slug: string;
  mer_slug_type: string;
  cat_slug: string;
  events: any[];
  promotions: any[];
  promo_slug: string;
  blog_title?: string;
  blog_url?: string;
  companyDomain: string;
}

export default function NavigationPill({
  merchantData,
  categories,
  mer_slug,
  mer_slug_type,
  cat_slug,
  events,
  promotions,
  promo_slug,
  blog_title,
  blog_url,
  companyDomain,
}: Props) {
  const pathname = usePathname() || '';
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  const isActive = (item: string) => {
    switch (item) {
      case 'Home':
        return pathname === '/';
      case 'Stores':
        return pathname.startsWith('/all-stores');
      case 'Categories':
        return pathname.includes(`/${cat_slug}`);
      case 'Products':
        return pathname.startsWith('/all-products') || pathname.startsWith('/products');
      case 'Events':
        return pathname.startsWith('/events');
      case 'Promotions':
        return pathname.includes(`/${promo_slug}`);
      default:
        return pathname === blog_url;
    }
  };

  const navItems = [
    { key: 'Home', href: '/', hasDropdown: false },
    { key: 'Stores', href: '/all-stores/A', hasDropdown: true },
    { key: 'Categories', href: `/${cat_slug}`, hasDropdown: true },
    { key: 'Products', href: '/all-products', hasDropdown: true },
    ...(events.length > 0 ? [{ key: 'Events', href: '/events', hasDropdown: true }] : []),
    ...(promotions.length > 0 ? [{ key: 'Promotions', href: `/${promo_slug}`, hasDropdown: true }] : []),
    ...(blog_title && blog_url ? [{ key: blog_title, href: blog_url, hasDropdown: false }] : []),
  ];

  // Position dropdown to avoid viewport overflow.
  // Stores is 2nd item (left side) → anchor left. Categories is 3rd item (right side) → anchor right.
  const getDropdownPosition = (itemKey: string, index: number) => {
    if (itemKey === 'Stores') return 'right-0';
    if (itemKey === 'Categories') return 'right-0';
    const isLastOrNearEnd = index >= navItems.length - 2;
    if (isLastOrNearEnd) return 'right-0';
    return 'left-0';
  };

  const dropdownVariants: Variants = {
    hidden: { opacity: 0, y: -10, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 400,
        damping: 25,
        staggerChildren: 0.05,
        delayChildren: 0.05,
      },
    },
    exit: {
      opacity: 0,
      y: -10,
      scale: 0.95,
      transition: { duration: 0.2 },
    },
  };

  const itemVariants: Variants = {
    hidden: { opacity: 0, x: -10 },
    visible: { opacity: 1, x: 0 },
  };

  const renderDropdown = (itemKey: string) => {
    switch (itemKey) {
      case 'Stores':
        return (
          <div className="grid grid-cols-2 gap-1.5 w-[420px] max-w-[85vw]">
            {merchantData?.slice(0, 10).map((item, idx) => (
              <motion.div key={idx} variants={itemVariants} whileHover={{ scale: 1.02, x: 2 }}>
                <Link
                  href={getMerchantHref(item, mer_slug, mer_slug_type)}
                  className="flex items-center gap-2 p-2 rounded-lg transition-all duration-300 bg-white/5 hover:bg-gradient-to-r hover:from-[#39ff14]/20 hover:to-transparent border border-white/10 hover:border-[#39ff14]/30 group/item"
                >
                  <div className="relative w-8 h-8 rounded-full bg-white/5 border border-white/20 overflow-hidden shadow-md flex-shrink-0">
                    {item?.merchant_logo ? (
                      <Image
                        src={getBaseImageUrl(companyDomain, item?.merchant_logo, '')}
                        alt={item?.merchant_name}
                        fill
                        className="object-contain p-1"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/40 text-[8px] font-bold">
                        LOGO
                      </div>
                    )}
                  </div>
                  <span className="text-xs font-bold tracking-wide text-white/90 group-hover/item:text-[#39ff14] transition-colors truncate">
                    {item.merchant_name}
                  </span>
                </Link>
              </motion.div>
            ))}
            {merchantData?.length > 10 && (
              <motion.div variants={itemVariants} className="col-span-2 mt-1 pt-1 border-t border-white/10">
                <Link
                  href="/all-stores/A"
                  className="block text-center py-1.5 text-[10px] font-black tracking-[0.15em] text-white/60 hover:text-[#39ff14] transition-all duration-300 uppercase"
                >
                  View All Brands →
                </Link>
              </motion.div>
            )}
          </div>
        );

      case 'Categories':
        return (
          <div className="grid grid-cols-2 gap-1.5 w-[420px] max-w-[85vw]">
            {categories?.slice(0, 10).map((item: any, idx: number) => (
              <motion.div key={idx} variants={itemVariants}>
                <Link
                  href={`/${item?.url}`}
                  className="flex items-center gap-2 p-2 rounded-lg transition-all duration-300 bg-white/5 hover:bg-gradient-to-r hover:from-[#39ff14]/20 hover:to-transparent border border-white/10 hover:border-[#39ff14]/30 group/item"
                >
                  <div className="relative w-7 h-7 rounded-md bg-white/5 border border-white/20 overflow-hidden flex items-center justify-center p-1">
                    <Image
                      src={getBaseImageUrl(companyDomain, item?.category_image, '')}
                      alt={item?.name}
                      width={16}
                      height={16}
                      className="object-contain brightness-0 invert opacity-70 group-hover/item:opacity-100 group-hover/item:sepia group-hover/item:saturate-[100] transition-all duration-300"
                    />
                  </div>
                  <span className="text-xs font-bold tracking-wide text-white/90 group-hover/item:text-[#39ff14] transition-colors truncate">
                    {item.name}
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        );

      case 'Products':
        return (
          <div className="min-w-[180px] max-w-[85vw]">
            <Link
              href="/products"
              className="flex items-center justify-between p-2 rounded-lg transition-all duration-300 bg-white/5 hover:bg-gradient-to-r hover:from-[#39ff14]/20 hover:to-transparent border border-white/10 hover:border-[#39ff14]/30 group/item"
            >
              <span className="text-xs font-bold tracking-wide text-white/90 group-hover/item:text-[#39ff14]">
                Brands Products
              </span>
              <span className="text-[#39ff14] text-[10px] opacity-0 group-hover/item:opacity-100 transition-all duration-300">
                →
              </span>
            </Link>
          </div>
        );

      case 'Events':
        return (
          <div className="min-w-[220px] max-w-[85vw] flex flex-col gap-1.5">
            {events.slice(0, 5).map((item, idx) => (
              <motion.div key={idx} variants={itemVariants}>
                <Link
                  href={getEventsHref(item, 'slug')}
                  className="flex items-center justify-between px-3 py-2 rounded-lg transition-all duration-300 bg-white/5 hover:bg-gradient-to-r hover:from-[#39ff14]/20 hover:to-transparent border border-white/10 hover:border-[#39ff14]/30 group/item"
                >
                  <span className="text-xs font-bold tracking-wide text-white/90 group-hover/item:text-[#39ff14] truncate">
                    {item.name}
                  </span>
                  <span className="text-[#39ff14] text-[10px] opacity-0 group-hover/item:opacity-100 transition-all duration-300 ml-2 flex-shrink-0">
                    →
                  </span>
                </Link>
              </motion.div>
            ))}
            {events.length > 5 && (
              <motion.div variants={itemVariants} className="mt-0.5 pt-1.5 border-t border-white/10">
                <Link
                  href="/events"
                  className="block text-center py-1.5 text-[10px] font-black tracking-[0.15em] text-[#39ff14] hover:text-white/90 transition-all duration-300 uppercase"
                >
                  View All Events →
                </Link>
              </motion.div>
            )}
          </div>
        );

      case 'Promotions':
        return (
          <div className="min-w-[220px] max-w-[85vw] flex flex-col gap-1.5">
            {promotions.slice(0, 5).map((item, idx) => (
              <motion.div key={idx} variants={itemVariants}>
                <Link
                  href={getPromotionHref(item, promo_slug)}
                  className="flex items-center justify-between px-3 py-2 rounded-lg transition-all duration-300 bg-white/5 hover:bg-gradient-to-r hover:from-[#39ff14]/20 hover:to-transparent border border-white/10 hover:border-[#39ff14]/30 group/item"
                >
                  <span className="text-xs font-bold tracking-wide text-white/90 group-hover/item:text-[#39ff14] truncate">
                    {item.name}
                  </span>
                  <span className="text-[#39ff14] text-[10px] opacity-0 group-hover/item:opacity-100 transition-all duration-300 ml-2 flex-shrink-0">
                    →
                  </span>
                </Link>
              </motion.div>
            ))}
            {promotions.length > 5 && (
              <motion.div variants={itemVariants} className="mt-0.5 pt-1.5 border-t border-white/10">
                <Link
                  href={`/${promo_slug}`}
                  className="block text-center py-1.5 text-[10px] font-black tracking-[0.15em] text-[#39ff14] hover:text-white/90 transition-all duration-300 uppercase"
                >
                  View All Deals →
                </Link>
              </motion.div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <nav className="relative z-50 w-full overflow-visible">
      <div className="flex flex-wrap items-center justify-center gap-0.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#0a0f0a]/90 via-[#0b110a]/95 to-[#0a0f0a]/90 backdrop-blur-xl border border-white/15 shadow-2xl shadow-black/20 overflow-visible">
        {navItems.map((item, idx) => {
          const active = isActive(item.key);
          const isHovered = hoveredItem === item.key;

          return (
            <div
              key={item.key}
              className="relative"
              style={{ overflow: 'visible' }}
              onMouseEnter={() => item.hasDropdown && setHoveredItem(item.key)}
              onMouseLeave={() => item.hasDropdown && setHoveredItem(null)}
            >
              <Link
                href={item.href}
                className={`
                  relative block px-3 py-1.5 text-xs font-bold tracking-[0.15em] uppercase
                  transition-all duration-300 rounded-full whitespace-nowrap
                  ${active ? 'text-white' : 'text-white/70 hover:text-white'}
                `}
              >
                {active && (
                  <motion.span
                    layoutId="activePill"
                    className="absolute inset-0 rounded-full bg-gradient-to-r from-[#39ff14]/20 to-[#39ff14]/5 shadow-[0_0_10px_rgba(57,255,20,0.3)] -z-0"
                    transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                  />
                )}
                <span className="relative z-10 flex items-center gap-1">
                  {item.key}
                  {item.hasDropdown && (
                    <motion.span
                      animate={{ rotate: isHovered ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <FontAwesomeIcon icon={faChevronDown} className="w-2 h-2" />
                    </motion.span>
                  )}
                </span>
              </Link>

              {item.hasDropdown && (
                <AnimatePresence>
                  {isHovered && (
                    <motion.div
                      variants={dropdownVariants}
                      initial="hidden"
                      animate="visible"
                      exit="exit"
                      className={`absolute top-full pt-2 z-50 ${getDropdownPosition(item.key, idx)}`}
                      style={{ transformOrigin: 'top center' }}
                    >
                      <div className="bg-gradient-to-br from-[#0b110a]/95 to-[#0a0f0a]/95 backdrop-blur-xl rounded-xl border border-white/15 shadow-2xl p-3 overflow-visible">
                        <div className="absolute -top-16 -right-16 w-32 h-32 bg-[#39ff14]/10 rounded-full blur-3xl pointer-events-none" />
                        <div className="absolute -bottom-16 -left-16 w-32 h-32 bg-[#39ff14]/5 rounded-full blur-3xl pointer-events-none" />
                        {renderDropdown(item.key)}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              )}
            </div>
          );
        })}
      </div>
    </nav>
  );
}